import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Leaf, 
  Sprout, 
  FlaskConical, 
  Shield, 
  Award, 
  ArrowRight,
  Check,
  Droplets,
  Youtube,
  Instagram,
  Play,
  FileText,
  Download
} from 'lucide-react';

export const HomePage = () => {
  const { t } = useTranslation();
  const heroImage = "https://images.unsplash.com/photo-1601593748611-3639718a6b0a?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";
  const labImage = "https://images.unsplash.com/photo-1709532390940-5c09fcdf098d?crop=entropy&cs=srgb&fm=jpg&w=800&q=85";
  const brochureUrl = "/brochures/stigmaflora-brochure.pdf";

  const openBrochure = () => {
    window.open(brochureUrl, '_blank', 'noopener,noreferrer');
  };

  const categories = [
    {
      icon: <Sprout className="w-8 h-8" />,
      title: t('home.categories.bio_title'),
      queryVal: 'Bio Stimulants',
      count: t('home.categories.bio_count'),
      description: t('home.categories.bio_desc'),
      color: 'bg-emerald-500',
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: t('home.categories.micro_title'),
      queryVal: 'Micro Nutrient Fertilizers',
      count: t('home.categories.micro_count'),
      description: t('home.categories.micro_desc'),
      color: 'bg-amber-500',
    },
    {
      icon: <FlaskConical className="w-8 h-8" />,
      title: t('home.categories.water_title'),
      queryVal: 'Water Soluble Fertilizers',
      count: t('home.categories.water_count'),
      description: t('home.categories.water_desc'),
      color: 'bg-teal-500',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('home.categories.ipm_title'),
      queryVal: 'IPM Tools',
      count: t('home.categories.ipm_count'),
      description: t('home.categories.ipm_desc'),
      color: 'bg-rose-500',
    },
  ];

  const features = [
    t('home.feat_1'),
    t('home.feat_2'),
    t('home.feat_3'),
    t('home.feat_4'),
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-full mb-6 animate-fade-in-up">
              {t('home.certified_badge')}
            </span>
            
            <h1 
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white font-['Outfit'] tracking-tight leading-tight mb-2 animate-fade-in-up animation-delay-100"
            >
              {t('home.hero_title')}
            </h1>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-emerald-400 font-['Outfit'] mb-4 animate-fade-in-up animation-delay-100">
              {t('home.hero_subtitle')}
            </h2>
            <p className="text-xl md:text-2xl text-white font-medium mb-6 animate-fade-in-up animation-delay-200">
              {t('home.hero_tagline')}
            </p>
            
            <p className="text-lg text-emerald-100 mb-8 max-w-xl animate-fade-in-up animation-delay-200">
              {t('home.hero_desc')}
            </p>
            
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/products">
                <Button
                  data-testid="hero-explore-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold btn-shadow"
                >
                  {t('home.explore')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button
                data-testid="hero-brochure-btn"
                onClick={openBrochure}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg font-semibold cursor-pointer"
              >
                <FileText className="mr-2 w-5 h-5" />
                {t('home.view_brochure')}
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-4 block">
              {t('home.cat_subtitle')}
            </span>
            <h2 
              data-testid="categories-title"
              className="text-3xl md:text-5xl font-semibold text-slate-900 font-['Outfit'] tracking-tight"
            >
              {t('home.cat_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Link
                key={index}
                to={`/products?category=${encodeURIComponent(category.queryVal)}`}
                data-testid={`category-card-${index}`}
              >
                <Card className="group h-full border-emerald-100/50 hover:border-emerald-200 bg-white hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden">
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 ${category.color} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                      {category.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 font-['Outfit'] mb-2">
                      {category.title}
                    </h3>
                    <p className="text-sm text-amber-600 font-medium mb-3">
                      {category.count}
                    </p>
                    <p className="text-slate-600 text-sm">
                      {category.description}
                    </p>
                    <div className="mt-4 flex items-center text-emerald-700 font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      {t('home.view_products')} <ArrowRight className="ml-1 w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src={labImage}
                  alt="Agricultural Expert in Field"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/60 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl max-w-[200px]">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="w-8 h-8 text-amber-500" />
                  <span className="text-2xl font-bold text-emerald-900 font-['Outfit']">ISO</span>
                </div>
                <p className="text-sm text-slate-600">9001:2015 Certified Quality Standards</p>
              </div>
            </div>

            <div>
              <span className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-4 block">
                {t('home.why_subtitle')}
              </span>
              <h2 
                data-testid="why-choose-title"
                className="text-3xl md:text-5xl font-semibold text-slate-900 font-['Outfit'] tracking-tight mb-6"
              >
                {t('home.why_title_1')}<br />{t('home.why_title_2')}
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {t('home.why_desc')}
              </p>

              <ul className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-700" />
                    </div>
                    <span className="text-slate-700 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-3">
                <Link to="/about">
                  <Button
                    data-testid="learn-more-btn"
                    className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full px-6 py-5 font-semibold btn-shadow"
                  >
                    {t('home.learn_more')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Button
                  data-testid="download-brochure-btn"
                  variant="outline"
                  onClick={openBrochure}
                  className="border-2 border-emerald-900 text-emerald-900 hover:bg-emerald-50 rounded-full px-6 py-5 font-semibold cursor-pointer"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t('home.download_brochure')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-4 block">
              {t('home.yt_subtitle')}
            </span>
            <h2 
              data-testid="youtube-title"
              className="text-3xl md:text-4xl font-semibold text-slate-900 font-['Outfit'] tracking-tight mb-4"
            >
              {t('home.yt_title')}
            </h2>
            <p className="text-base text-slate-600 max-w-xl mx-auto">
              {t('home.yt_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Main Video - Auto-playing muted */}
            <div className="lg:col-span-2">
              <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-video shadow-xl">
                <iframe
                  data-testid="youtube-main-video"
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/videoseries?list=UUZbaZT7Blkexm-qbvY3Hg7A&autoplay=1&mute=1&loop=1&playlist=UUZbaZT7Blkexm-qbvY3Hg7A"
                  title="Stigmaflora Agri Science Videos"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Channel Info */}
            <div className="space-y-4">
              <Card className="border-red-100 bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mb-4">
                    <Youtube className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2">
                    {t('home.yt_channel_title')}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {t('home.yt_channel_desc')}
                  </p>
                  <Button 
                    data-testid="youtube-subscribe-btn"
                    onClick={() => window.open('https://www.youtube.com/channel/UCZbaZT7Blkexm-qbvY3Hg7A?sub_confirmation=1', '_blank', 'noopener,noreferrer')}
                    className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full cursor-pointer"
                  >
                    <Youtube className="w-4 h-4 mr-2" />
                    {t('home.yt_subscribe')}
                  </Button>
                  <Button 
                    data-testid="instagram-follow-btn"
                    onClick={() => window.open('https://www.instagram.com/stigmaflora', '_blank', 'noopener,noreferrer')}
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full cursor-pointer mt-3"
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    {t('home.ig_follow')}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-emerald-100">
                <CardContent className="p-5">
                  <h4 className="font-semibold text-slate-900 font-['Outfit'] mb-3 text-sm">
                    {t('home.yt_learn_title')}
                  </h4>
                  <ul className="space-y-2">
                    {[
                      t('home.yt_learn_1'),
                      t('home.yt_learn_2'),
                      t('home.yt_learn_3'),
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                        <Play className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Button
                data-testid="youtube-view-all-btn"
                variant="outline"
                onClick={() => window.open('https://www.youtube.com/channel/UCZbaZT7Blkexm-qbvY3Hg7A', '_blank', 'noopener,noreferrer')}
                className="w-full border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full cursor-pointer"
              >
                {t('home.yt_view_all')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-emerald-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            data-testid="cta-title"
            className="text-3xl md:text-5xl font-semibold text-white font-['Outfit'] tracking-tight mb-6"
          >
            {t('home.cta_title')}
          </h2>
          <p className="text-lg text-emerald-200 mb-8 leading-relaxed max-w-2xl mx-auto">
            {t('home.cta_desc')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                {t('home.cta_contact')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:7702099393">
              <Button
                data-testid="cta-call-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
              >
                {t('home.cta_call')}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
